import React, { useCallback, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { useResponsive } from '../../hooks/useResponsive';

import {
  formatShortDateByLanguage,
} from '../../services/language.utils';

import arrowRightDisabledIcon from '../../assets/chevron-disabled.svg';
import arrowRightIcon from '../../assets/chevron-white-right.svg';

import { CoverageHeaderLeft } from './CoverageHeaderLeft';
import { CoveragePetsSection } from './CoveragePetsSection';
import { CoveragePetsNoAction } from './CoveragePetsNoAction';
import { PolicyTopInformation } from './PolicyTopInformation';
import { ReviewPetRolloverModal } from './ReviewPetRolloverModal';
import { EditWarningModal } from './EditWarningModal';
import { UnsavedInformationNotice } from './UnsavedInformationNotice';
import { CompleteInfoButton, CoveragePeriodStatus } from './common';
import FigoLottie from '../common/FigoLottie';

import { PET_DEMOS_SPECIES } from '../../constants';
import { calculateRolloverMinimums } from './petDemographics.utils';

import styles from './PolicyInformation.module.css';

const PolicyInformation = ({
  adjustedPeriodCounts = new Map(),
  goToNextStep = () => { },
  isCoveragePeriodCompleted = false,
  onCoveragePeriodSelected = () => { },
  onSubmit = () => { },
  petsDataByCoveragePeriod = new Map(),
  policyData = {},
  setSelectedRolloverPetsForGroup = () => { },
  setSkippedRolloverGroupId = () => { },
  submitting = false,
}) => {
  const { t } = useTranslation('petDemos');
  const { isMobile } = useResponsive();
  const [rolloverModalContext, setRolloverModalContext] = useState(null);
  const [isEditWarningModalOpen, setIsEditWarningModalOpen] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [nextPeriodNumber, setNextPeriodNumber] = useState(null);

  const isEconsentSubmitted = policyData?.declarations?.isEconsentSubmitted;
  const coveragePeriods = policyData.NeedsPetDemographics || [];
  // Use adjusted counts (historically-ineligible) when available,
  // falling back to original pet counts
  const coveragePeriodsWithPets = coveragePeriods.map((period) => {
    const adjusted = adjustedPeriodCounts.get(period.groupId);

    if (adjusted) {
      return { ...period, ...adjusted };
    }

    const pets = period.pets ?? [];
    const numberOfCats = pets.filter(
      (pet) => pet.demographicData?.species === PET_DEMOS_SPECIES.cat,
    ).length;
    const numberOfDogs = pets.filter(
      (pet) => pet.demographicData?.species === PET_DEMOS_SPECIES.dog,
    ).length;

    return {
      ...period,
      numberOfCats,
      numberOfDogs,
      numberOfPets: pets.length,
    };
  });

  const noEligiblePetsTerm = useMemo(() => {
    let result = { noActions: false, term: null };

    coveragePeriodsWithPets.forEach((coveragePeriod) => {
      const policyTerm = coveragePeriod?.policyTerm;

      const previousPeriod = coveragePeriodsWithPets.find(
        (period) => period.policyTerm === policyTerm - 1,
      );

      const petsDataPrevPeriod = petsDataByCoveragePeriod
        .get(previousPeriod?.groupId) || [];

      const previousEligiblePets = petsDataPrevPeriod?.eligiblePets;

      // Track if there are no eligible pets in a term
      // to set future terms as "No Pending Action"
      if (previousEligiblePets?.length === 0) {
        result = { noActions: true, term: previousPeriod?.policyTerm ?? null };
      }
    });

    return result;
  }, [coveragePeriodsWithPets, petsDataByCoveragePeriod]);

  const hasIncompleteCoveragePeriod = useMemo(() => {
    // Enable submit button if there's "No Pending Action" status
    if (noEligiblePetsTerm.noActions) {
      return false;
    }

    // Normal flow disable submit button
    // when there are incomplete coverage periods
    return coveragePeriods.some((coveragePeriod) => !isCoveragePeriodCompleted(
      coveragePeriod.groupId,
      petsDataByCoveragePeriod,
    ));
  }, [
    coveragePeriods,
    isCoveragePeriodCompleted,
    noEligiblePetsTerm,
    petsDataByCoveragePeriod,
  ]);

  const handleRolloverSave = useCallback(
    (selectedPets) => {
      if (rolloverModalContext?.coveragePeriod) {
        setSelectedRolloverPetsForGroup(
          rolloverModalContext.coveragePeriod.groupId,
          selectedPets,
        );
      }

      onCoveragePeriodSelected(rolloverModalContext?.coveragePeriod);
      goToNextStep();
      setRolloverModalContext(null);
    },
    [
      goToNextStep,
      onCoveragePeriodSelected,
      rolloverModalContext,
      setSelectedRolloverPetsForGroup,
    ],
  );

  const handleRolloverSkip = useCallback(() => {
    const coveragePeriod = rolloverModalContext?.coveragePeriod;
    const groupId = coveragePeriod?.groupId;

    if (groupId) {
      setSkippedRolloverGroupId(groupId);
      setSelectedRolloverPetsForGroup(groupId, []);
    }
    onCoveragePeriodSelected(coveragePeriod);
    goToNextStep();
    setRolloverModalContext(null);
  }, [
    goToNextStep,
    onCoveragePeriodSelected,
    rolloverModalContext,
    setSelectedRolloverPetsForGroup,
    setSkippedRolloverGroupId,
  ]);

  const handleProceed = useCallback(() => {
    if (selectedPeriod) {
      onCoveragePeriodSelected(selectedPeriod);
    }

    goToNextStep();
    setIsEditWarningModalOpen(false);
    setSelectedPeriod(null);
  }, [
    goToNextStep,
    onCoveragePeriodSelected,
    selectedPeriod,
  ]);

  const onCompleteInformationClick = useCallback(
    ({
      coveragePeriod,
      isPeriodCompleted,
      minimumCats,
      minimumDogs,
      nextTerm,
      previousEligiblePets,
      showRolloverModal,
    }) => () => {
      setSelectedPeriod(coveragePeriod);
      setNextPeriodNumber(nextTerm);

      if (showRolloverModal) {
        setRolloverModalContext({
          coveragePeriod,
          minimumCats,
          minimumDogs,
          numberOfCats: coveragePeriod.maxRolloverCats
            ?? coveragePeriod.numberOfCats ?? 0,
          numberOfDogs: coveragePeriod.maxRolloverDogs
            ?? coveragePeriod.numberOfDogs ?? 0,
          previousEligiblePets,
        });

        return;
      }

      if (isPeriodCompleted && nextTerm && !isEconsentSubmitted) {
        setIsEditWarningModalOpen(true);
      } else {
        onCoveragePeriodSelected(coveragePeriod);
        goToNextStep();
      }
    },
    [goToNextStep, onCoveragePeriodSelected],
  );

  const onCloseEditWarningModal = useCallback(() => {
    setIsEditWarningModalOpen(false);
    setSelectedPeriod(null);
  }, []);

  const onCloseRolloverModal = useCallback(() => {
    setRolloverModalContext(null);
  }, []);

  return (
    <div className={styles.policyContent}>
      <ReviewPetRolloverModal
        coveragePeriod={rolloverModalContext?.coveragePeriod}
        isOpen={Boolean(rolloverModalContext)}
        minimumCats={rolloverModalContext?.minimumCats}
        minimumDogs={rolloverModalContext?.minimumDogs}
        numberOfCats={rolloverModalContext?.numberOfCats}
        numberOfDogs={rolloverModalContext?.numberOfDogs}
        onClose={onCloseRolloverModal}
        onSave={handleRolloverSave}
        onSkip={handleRolloverSkip}
        previousEligiblePets={rolloverModalContext?.previousEligiblePets}
      />

      <PolicyTopInformation policyData={policyData} />

      <div className={styles.policyActions}>
        <h4 className={styles.policyActionsTitle}>
          <Trans
            components={{
              coverages: <span className={styles.policyActionsTitleBlue} />,
            }}
            i18nKey="policyInformation.policyActionsCardHeading"
            ns="petDemos"
            values={{
              coverages: coveragePeriods.length,
            }}
          />
        </h4>

        <div className={styles.coveragePeriodsContainer}>
          {coveragePeriodsWithPets.map((coveragePeriod, index) => {
            const policyTerm = coveragePeriod?.policyTerm;

            const currentPeriod = petsDataByCoveragePeriod
              .get(coveragePeriod.groupId);

            const allPets = currentPeriod?.allPets || [];
            const ineligiblePets = currentPeriod?.ineligiblePets || [];
            const ineligiblePetsCounts = currentPeriod?.ineligiblePetsCounts;
            const removedPets = currentPeriod?.removedPets || [];
            const removedPetsCounts = currentPeriod?.removedPetsCounts;
            const initialCoveragePets = coveragePeriod?.pets || [];

            // Eligible pets from the previous period (by policyTerm)
            const previousPeriod = coveragePeriodsWithPets.find(
              (period) => period.policyTerm === policyTerm - 1,
            );
            const petsDataPrevPeriod = petsDataByCoveragePeriod
              .get(previousPeriod?.groupId) || [];

            const previousEligiblePets = petsDataPrevPeriod?.eligiblePets;
            const prevEligiblePetsCounts = petsDataPrevPeriod
              ?.eligiblePetsCounts;

            // Track if there are no eligible pets in a term
            // to mark future terms as "No Pending Action"
            const noPendingAction = noEligiblePetsTerm.noActions
              && policyTerm > noEligiblePetsTerm.term;

            // Calculate if this coverage period is completed
            const isPeriodCompleted = noEligiblePetsTerm.noActions
              || isCoveragePeriodCompleted(
                coveragePeriod.groupId,
                petsDataByCoveragePeriod,
              );

            // Find if there's a next period after current
            const nextPeriod = coveragePeriodsWithPets.find(
              (period) => period.policyTerm === policyTerm + 1,
            );
            const nextTerm = nextPeriod?.policyTerm;

            const showRolloverModal = !isEconsentSubmitted
              && policyTerm > 1 && !isPeriodCompleted
              && !allPets.length
              && prevEligiblePetsCounts?.total > 0;

            const { minimumCats, minimumDogs } = calculateRolloverMinimums({
              coverageStartDate: coveragePeriod.coverageStartDate,
              eligibleCats: prevEligiblePetsCounts?.cats,
              eligibleDogs: prevEligiblePetsCounts?.dogs,
              numberOfCats: coveragePeriod.maxRolloverCats
                ?? coveragePeriod.numberOfCats ?? 0,
              numberOfDogs: coveragePeriod.maxRolloverDogs
                ?? coveragePeriod.numberOfDogs ?? 0,
              removalDates: coveragePeriod.removalDates,
            });

            // Check if any previous period is incomplete
            // Disable if there's an incomplete period before it
            const hasIncompletePreviousPeriod = coveragePeriodsWithPets
              .slice(0, index)
              .some((prevPeriod) => !isCoveragePeriodCompleted(
                prevPeriod.groupId,
                petsDataByCoveragePeriod,
              ));

            // Disable if this period is not completed
            // AND there's an incomplete period before it
            const isDisabled = !isPeriodCompleted
              && hasIncompletePreviousPeriod;

            return (
              <div
                key={`coverageCard-${coveragePeriod.groupId}`}
                className={isDisabled
                  ? styles.coveragePeriodCardDisabled
                  : styles.coveragePeriodCard}
                data-completed={isPeriodCompleted}
              >
                <div className={styles.policyActionCardHeader}>
                  <CoverageHeaderLeft
                    isCoveragePeriodCompleted={isPeriodCompleted}
                    noPendingAction={noPendingAction}
                    planEndDate={formatShortDateByLanguage(
                      coveragePeriod.coverageEndDate,
                    )}
                    planName={coveragePeriod.planName}
                    planStartDate={formatShortDateByLanguage(
                      coveragePeriod.coverageStartDate,
                    )}
                    policyTerm={policyTerm}
                  />

                  {isMobile ? null : (
                    <div className={styles.coveragePeriodHeaderRight}>
                      <CoveragePeriodStatus
                        isCoveragePeriodCompleted={isPeriodCompleted}
                        noPendingAction={noPendingAction}
                      />

                      <CompleteInfoButton
                        isCompleted={isPeriodCompleted}
                        isDisabled={isDisabled}
                        noPendingAction={noPendingAction}
                        onClick={onCompleteInformationClick({
                          coveragePeriod,
                          isPeriodCompleted,
                          minimumCats,
                          minimumDogs,
                          nextTerm,
                          previousEligiblePets,
                          showRolloverModal,
                        })}
                      />
                    </div>
                  )}
                </div>

                {noPendingAction
                  ? <CoveragePetsNoAction />
                  : (
                    <CoveragePetsSection
                      allPets={allPets}
                      ineligiblePets={ineligiblePets}
                      ineligiblePetsCounts={ineligiblePetsCounts}
                      initialCoveragePets={initialCoveragePets}
                      isCompleted={isPeriodCompleted}
                      numberOfCats={coveragePeriod.numberOfCats}
                      numberOfDogs={coveragePeriod.numberOfDogs}
                      numberOfPets={coveragePeriod.numberOfPets}
                      planName={coveragePeriod.planName}
                      previousEligiblePets={previousEligiblePets}
                      removedPets={removedPets}
                      removedPetsCounts={removedPetsCounts}
                    />
                  )}

                {isMobile ? (
                  <CompleteInfoButton
                    isCompleted={isPeriodCompleted}
                    isDisabled={isDisabled}
                    noPendingAction={noPendingAction}
                    onClick={onCompleteInformationClick({
                      coveragePeriod,
                      isPeriodCompleted,
                      minimumCats,
                      minimumDogs,
                      nextTerm,
                      previousEligiblePets,
                      showRolloverModal,
                    })}
                  />
                ) : null}
              </div>
            );
          })}
        </div>
      </div>

      {!hasIncompleteCoveragePeriod && <UnsavedInformationNotice />}

      <div className={styles.policyContinueButtonContainer}>
        <button
          className={styles.policyContinueButton}
          disabled={hasIncompleteCoveragePeriod}
          onClick={onSubmit}
          type="button"
        >
          {submitting ? <FigoLottie height={null} width={20} /> : (
            <>
              {t('policyInformation.submitInformation')}

              <img
                alt={t('imgAlts.chevronRight')}
                src={hasIncompleteCoveragePeriod
                  ? arrowRightDisabledIcon
                  : arrowRightIcon}
              />
            </>
          )}
        </button>
      </div>

      <EditWarningModal
        handleProceed={handleProceed}
        isOpen={isEditWarningModalOpen}
        nextPeriodNumber={nextPeriodNumber}
        onClose={onCloseEditWarningModal}
      />
    </div>
  );
};

export { PolicyInformation };
